import { type FormEvent, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { CheckCircle2, ShieldCheck } from "lucide-react";

import { Button } from "@components/ui/Button";

import {
  Auth,
  parseAuthResult,
  useAuthenticatedUser,
} from "@lib/authentication";
import type { TotpSetup } from "@lib/authentication/hooks/use-authentication/use-authentication.types";
import {
  TOTP_APP_NAME,
  twoFactorQueryKey,
  useTotpEnabled,
} from "@lib/authentication/two-factor";

import { TotpSetupDetails } from "./TotpSetupDetails";

// Set up two-factor sign-in with an authenticator app (OMG-2777). Cognito
// issues a secret; the admin scans it, proves it with one code, and TOTP
// becomes their preferred factor. Then every session for the account is
// signed out, so one started before two-factor cannot outlive it.

type Setup = TotpSetup;

export const TwoFactorPage = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { authenticatedUser } = useAuthenticatedUser();
  const { data: enabled, isLoading } = useTotpEnabled();
  const [setup, setSetup] = useState<Setup>();
  const [setupError, setSetupError] = useState<string>();
  const [code, setCode] = useState("");
  const [codeError, setCodeError] = useState<string>();
  const [isVerifying, setIsVerifying] = useState(false);
  const [done, setDone] = useState(false);
  const [signOutFailed, setSignOutFailed] = useState(false);
  const [codeAccepted, setCodeAccepted] = useState(false);
  const [turnOnError, setTurnOnError] = useState(false);
  // Already on, moving it to a new phone. In the admin pool (OMG-2777 v2)
  // everyone has it from their first sign-in, so this is the page's main use.
  // Cognito drops the old secret as soon as a new one is issued; until the
  // new one is verified the pool's MFA_SETUP step covers the next sign-in.
  const [replacing, setReplacing] = useState(false);

  useEffect(() => {
    if (isLoading || (enabled && !replacing) || setup) return;
    let cancelled = false;
    Auth.setUpTotp().then((result) => {
      if (cancelled) return;
      if (!result.success) {
        setSetupError(
          "Two-factor sign-in could not be started. It may not be switched on for this environment yet; ask engineering.",
        );
        return;
      }
      const uri = result.data
        .getSetupUri(TOTP_APP_NAME, authenticatedUser?.email)
        .toString();
      setSetup({ secret: result.data.sharedSecret, uri });
    });
    return () => {
      cancelled = true;
    };
  }, [isLoading, enabled, replacing, setup, authenticatedUser?.email]);

  const verify = async (event: FormEvent) => {
    event.preventDefault();
    const trimmed = code.replace(/\s/g, "");
    if (!/^\d{6}$/.test(trimmed)) {
      setCodeError("Enter the 6-digit code from your authenticator app.");
      return;
    }
    setIsVerifying(true);
    setCodeError(undefined);
    try {
      parseAuthResult(await Auth.verifyTotpSetup(trimmed));
    } catch {
      setCodeError(
        "That code did not work. Check the time on your phone is set automatically and try the newest code.",
      );
      setIsVerifying(false);
      return;
    }
    setCodeAccepted(true);
    await turnOn();
  };

  // The code is proven; make TOTP the account's factor, then revoke every
  // session for the account, straight away rather than on a button the admin
  // may never press, so none from before two-factor survives.
  const turnOn = async () => {
    setIsVerifying(true);
    setTurnOnError(false);
    const preferred = await Auth.preferTotp();
    if (!preferred.success) {
      setTurnOnError(true);
      setIsVerifying(false);
      return;
    }
    const signedOut = await Auth.signOutEverywhere();
    setSignOutFailed(!signedOut.success);
    queryClient.setQueryData(twoFactorQueryKey, true);
    setDone(true);
    setIsVerifying(false);
  };

  const signInAgain = () => {
    localStorage.removeItem("si");
    localStorage.removeItem("ck");
    queryClient.clear();
    navigate("/login");
  };

  if (isLoading) return <div>Loading...</div>;

  return (
    <div className="max-w-xl space-y-4">
      <h1 className="flex items-center gap-2 text-2xl font-semibold">
        <ShieldCheck /> Two-factor sign-in
      </h1>

      {done ? (
        <div className="space-y-4 rounded-lg border bg-white p-4">
          <p className="flex items-center gap-2 font-semibold text-green-700">
            <CheckCircle2 /> Two-factor sign-in is on.
          </p>
          <p>
            From now on you sign in with your password and a code from your
            authenticator app.
          </p>
          {signOutFailed ? (
            <p role="alert" className="text-red-700">
              Your other sessions could not be signed out and may still be live.
              Tell engineering so they can sign the account out from AWS.
            </p>
          ) : (
            <p>
              You have been signed out on every device, so a session from before
              cannot be reused.
            </p>
          )}
          <Button onClick={signInAgain}>Sign in again</Button>
        </div>
      ) : enabled && !replacing ? (
        <div className="space-y-2 rounded-lg border bg-white p-4">
          <p className="flex items-center gap-2 font-semibold text-green-700">
            <CheckCircle2 /> Two-factor sign-in is on for this account.
          </p>
          <p className="text-sm text-gray-600">
            New phone? Move your authenticator here, with the new phone in hand:
            starting switches the old phone&apos;s codes off at once. Lost your
            phone? Ask engineering to reset it.
          </p>
          <Button onClick={() => setReplacing(true)}>
            Move to a new phone
          </Button>
        </div>
      ) : (
        <div className="space-y-4 rounded-lg border bg-white p-4">
          {replacing ? (
            <p>
              Scan this with the authenticator app on your new phone and enter
              its code now. The old phone&apos;s codes no longer work; if you
              leave before this finishes, your next sign-in asks you to set up
              an authenticator again.
            </p>
          ) : (
            <p>
              Admin accounts need a code from an authenticator app (Google
              Authenticator, 1Password, Authy, Microsoft Authenticator) as well
              as a password. It takes about two minutes.
            </p>
          )}
          {setupError && (
            <p role="alert" className="text-red-700">
              {setupError}
            </p>
          )}
          {setup && (
            <>
              <ol className="list-decimal space-y-4 pl-5">
                <li>
                  <p>Scan this with your authenticator app.</p>
                  <TotpSetupDetails setup={setup} />
                </li>
                <li>
                  {codeAccepted ? (
                    <div className="space-y-2">
                      <p>Your code was accepted.</p>
                      {turnOnError ? (
                        <>
                          <p role="alert" className="text-sm text-red-700">
                            Turning two-factor sign-in on did not finish. Try
                            again; you do not need a new code.
                          </p>
                          <Button onClick={turnOn} disabled={isVerifying}>
                            Try again
                          </Button>
                        </>
                      ) : (
                        <p>Turning it on...</p>
                      )}
                    </div>
                  ) : (
                    <form onSubmit={verify} className="space-y-2">
                      <label htmlFor="totp-code" className="block">
                        Enter the 6-digit code it shows.
                      </label>
                      <input
                        id="totp-code"
                        value={code}
                        onChange={(e) => setCode(e.target.value)}
                        inputMode="numeric"
                        autoComplete="one-time-code"
                        className="block w-40 rounded-lg border-2 p-2 font-mono tracking-widest"
                        aria-invalid={!!codeError}
                      />
                      {codeError && (
                        <p role="alert" className="text-sm text-red-700">
                          {codeError}
                        </p>
                      )}
                      <Button type="submit" disabled={isVerifying}>
                        Turn on two-factor sign-in
                      </Button>
                    </form>
                  )}
                </li>
              </ol>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default TwoFactorPage;
